import { Loader } from '@googlemaps/js-api-loader'
import MarkerClusterer from '@googlemaps/markerclustererplus'
import { Calculator } from '@googlemaps/markerclustererplus/dist/markerclusterer'
import { DefaultMapCoordinates, DefaultMapWithMarkerZoom, DefaultNewMapZoom, ICoordinates } from '../../../../../../core/src'
import {
  clustererIconUrl,
  DefaultDesktopMaxZoom,
  DefaultDesktopMinZoom,
  DefaultMobileMaxZoom,
  DefaultMobileMinZoom
} from './constants'
import { MarkerIconFactory } from './factories/marker-icon.factory'
import { IMapOptions } from './interfaces/i-map-options'
import { QuiteLightMapStyle } from './styles/quite-light'

export * from '@googlemaps/markerclustererplus'

export class GoogleMapWrapper {
  async initialize(googleMapsApiKey: string): Promise<typeof google> {
    const loader = new Loader({
      apiKey: googleMapsApiKey,
      libraries: ['places']
    })
    return loader.load()
  }

  /**
   * Generates a new map object.
   * @todo Add types
   */
  createMap(mapId: string, coordinates: ICoordinates, options?: IMapOptions): google.maps.Map {
    const zoom = this.determineMapZoom(coordinates)
    const map = new google.maps.Map(document.getElementById(mapId)!, {
      center: {
        // This is redudant but if we don't do this, the google maps API
        // throws out an error
        lat: parseFloat(coordinates.lat!.toString()),
        lng: parseFloat(coordinates.lng!.toString())
      },
      zoom
    })
    map.setOptions({ styles: QuiteLightMapStyle, ...options })
    return map
  }
  /**
   * Creates a new marker in a given map.
   * @todo Add types
   */
  createMarker(map: google.maps.Map, coordinates: ICoordinates, draggable: boolean = false): google.maps.Marker {
    return new google.maps.Marker({
      position: {
        // This is redudant but if we don't do this, the google maps API
        // throws out an error
        lat: parseFloat(coordinates.lat!.toString()),
        lng: parseFloat(coordinates.lng!.toString())
      },
      icon: MarkerIconFactory.defaultMarkerIcon(),
      map,
      draggable
    })
  }
  /**
   * Creates a new marker in a given map.
   * @todo Add types
   */
  createCenterMarker(map: google.maps.Map, coordinates: ICoordinates, draggable: boolean = false): google.maps.Marker {
    return new google.maps.Marker({
      position: {
        // This is redudant but if we don't do this, the google maps API
        // throws out an error
        lat: parseFloat(coordinates.lat!.toString()),
        lng: parseFloat(coordinates.lng!.toString())
      },
      icon: MarkerIconFactory.userLocationIcon(),
      map,
      draggable
    })
  }
  /**
   * Creats a marker cluster
   */
  createMarkerClusterer(map: google.maps.Map, calculator?: Calculator): MarkerClusterer {
    const options = {
      imagePath: 'https://developers.google.com/maps/documentation/javascript/examples/markerclusterer/m',
      styles: [
        { url: clustererIconUrl, height: 69, width: 50, fontWeight: 'bold', textSize: 18, anchorText: <[number, number]>[15, 0] }
      ]
      // There are problems in using this custom styles so for now we will use the example from google developer so that we can proceed with the other tasks
      // URL only works on https links (example shown below) not through project links (eg. ./assets/<file_name>)
      // Marker count text is not centered. Need to look for the documentation of the styles properties of the MarkerClusterer.
      // styles:[{
      //   url: "https://unpkg.com/@googlemaps/markerclustererplus@1.0.3/images/m3.png",
      //   width: 66,
      //   height: 65,
      //   // fontFamily: "comic sans ms",
      //   textSize: 15,
      // }]
    }
    const clusterer = new MarkerClusterer(map!, [], options)
    if (calculator) clusterer.setCalculator(calculator)
    return clusterer
  }
  /**
   * Create an autocomplete field to do google map searches.
   * @todo Add types
   */
  createAutocomplete(mapId: string): google.maps.places.Autocomplete | undefined {
    const input = document.getElementById(`${mapId}-autocomplete`) as HTMLInputElement
    if (!input) return undefined
    const options = {
      componentRestrictions: { country: 'jp' },
      origin: DefaultMapCoordinates,
      strictBounds: true
    }
    return new google.maps.places.Autocomplete(input, options)
  }
  /**
   * Creates a the info window in the map.
   */
  createInfoWindow(maxWidth: number = 300): google.maps.InfoWindow {
    // eslint-disable-next-line no-undef
    return new google.maps.InfoWindow({
      maxWidth
    })
  }
  /**
   * Returns a valid coordinates data
   * @param coordinates
   * @returns
   */
  determineCoordinates(coordinates: ICoordinates): ICoordinates {
    return coordinates || { latitude: DefaultMapCoordinates.lat, longitude: DefaultMapCoordinates.lng }
  }
  /**
   * Determines the map's zoom according to the provided coordinates. If
   * the coordinates are the same as the default coordinates, a default zoom is used for new maps.
   * Otherwise a zoom for a map with an already set location is returned.
   */
  determineMapZoom(coordinates: ICoordinates): number {
    let zoom = DefaultMapWithMarkerZoom
    const lngStr = coordinates.lng?.toString()
    const latStr = coordinates.lat?.toString()
    const defaultLngStr = DefaultMapCoordinates.lng.toString()
    const defaultLatStr = DefaultMapCoordinates.lat.toString()
    if (lngStr === defaultLngStr && latStr === defaultLatStr) zoom = DefaultNewMapZoom
    return zoom
  }
  /**
   * Creates a map options object with default options.
   * @param isMobile
   * @returns
   */
  getMapOptions(isMobile: boolean): IMapOptions {
    const options = { 
      maxZoom: DefaultDesktopMaxZoom, 
      minZoom: DefaultDesktopMinZoom, 
      disableDefaultUI: true,
      zoomControl: true
    } as IMapOptions
    if (isMobile) {
      options.minZoom = DefaultMobileMinZoom
      options.maxZoom = DefaultMobileMaxZoom
    }
    return options
  }
}
